import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tag, Zap, Loader2, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/time";

/**
 * Promote tab — lets a dispensary owner:
 *  - Toggle a Deal of the Day on any product (set original_price)
 *  - Buy a Boost on any product (Stripe checkout, 7 or 30 days)
 */
export const PromoteTab = ({ products, onRefresh }) => {
  const [dealFor, setDealFor] = useState(null);
  const [boostFor, setBoostFor] = useState(null);

  return (
    <div>
      {/* Active campaigns summary */}
      <ActiveSummary products={products} />

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Deal of the Day</th>
              <th className="px-4 py-3 font-medium">Boost</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr
                key={p.id}
                className="border-t border-border"
                data-testid={`promote-row-${p.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-serif text-base">{p.strain_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.brand} · {p.category} · {p.unit}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="price-tag text-base">${p.price.toFixed(2)}</div>
                  {p.deal_active && (
                    <div className="text-xs text-muted-foreground line-through">
                      ${p.original_price?.toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {p.deal_active ? (
                    <div className="flex items-center gap-2">
                      <span className="pill bg-rose-500/15 text-rose-300 border-rose-500/40 text-xs">
                        <Tag className="w-3 h-3" />
                        {p.deal_label || "Active"}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`deal-clear-${p.id}`}
                        onClick={async () => {
                          try {
                            await api.put(`/dispensary/products/${p.id}/deal`, {
                              deal_active: false,
                            });
                            toast.success("Deal cleared");
                            onRefresh();
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                        className="h-8 text-xs"
                      >
                        End deal
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      data-testid={`deal-open-${p.id}`}
                      onClick={() => setDealFor(p)}
                      variant="outline"
                      className="rounded-full h-8 text-xs bg-transparent border-border"
                    >
                      <Tag className="w-3 h-3 mr-1.5" /> Add deal
                    </Button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {p.boosted ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="pill text-xs"
                        style={{
                          color: "#D4A268",
                          borderColor: "rgba(212,162,104,0.5)",
                          background: "rgba(212,162,104,0.12)",
                        }}
                      >
                        <Zap className="w-3 h-3" />
                        Boosted
                        {p.boost_expires_at && (
                          <span className="text-muted-foreground ml-1">
                            · ends {timeAgo(p.boost_expires_at)?.replace(" ago", "")}
                          </span>
                        )}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`boost-cancel-${p.id}`}
                        onClick={async () => {
                          try {
                            await api.delete(
                              `/dispensary/products/${p.id}/boost`
                            );
                            toast.success("Boost canceled");
                            onRefresh();
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                        className="h-8 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      data-testid={`boost-open-${p.id}`}
                      onClick={() => setBoostFor(p)}
                      className="rounded-full h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Zap className="w-3 h-3 mr-1.5" /> Boost
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DealDialog
        product={dealFor}
        onClose={() => setDealFor(null)}
        onSaved={() => {
          setDealFor(null);
          onRefresh();
        }}
      />
      <BoostDialog
        product={boostFor}
        onClose={() => setBoostFor(null)}
      />
    </div>
  );
};

const ActiveSummary = ({ products }) => {
  const dealCount = products.filter((p) => p.deal_active).length;
  const boostCount = products.filter((p) => p.boosted).length;
  return (
    <div className="grid sm:grid-cols-3 gap-5 mb-6">
      <Card icon={<Tag className="w-4 h-4" />} title="Active deals" value={dealCount}>
        Free with subscription
      </Card>
      <Card icon={<Zap className="w-4 h-4" />} title="Boosted products" value={boostCount}>
        Top of search · paid
      </Card>
      <Card icon={<CheckCircle2 className="w-4 h-4" />} title="Total products" value={products.length}>
        On your menu
      </Card>
    </div>
  );
};

const Card = ({ icon, title, value, children }) => (
  <div className="border border-border rounded-2xl bg-card p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
      <span className="text-primary">{icon}</span>
      {title}
    </div>
    <div className="font-serif text-3xl mt-2">{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{children}</div>
  </div>
);

const DealDialog = ({ product, onClose, onSaved }) => {
  const [originalPrice, setOriginalPrice] = useState("");
  const [label, setLabel] = useState("Deal of the Day");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (product) {
      setOriginalPrice((product.price * 1.25).toFixed(2));
      setLabel("Deal of the Day");
    }
  }, [product]);

  if (!product) return null;
  const orig = parseFloat(originalPrice);
  const valid = !Number.isNaN(orig) && orig > product.price;
  const discount = valid
    ? Math.round(((orig - product.price) / orig) * 100)
    : 0;

  const submit = async () => {
    setLoading(true);
    try {
      await api.put(`/dispensary/products/${product.id}/deal`, {
        deal_active: true,
        original_price: orig,
        deal_label: label,
      });
      toast.success(`Deal activated — ${discount}% off`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogTitle className="font-serif text-2xl">Add a deal</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Set what this would cost without the discount. The deal price stays
          your current price (${product.price.toFixed(2)}).
        </DialogDescription>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Original price (was-price)
            </Label>
            <Input
              data-testid="deal-original-price"
              type="number"
              step="0.01"
              value={originalPrice}
              onChange={(e) => setOriginalPrice(e.target.value)}
              className="h-11 mt-1.5"
            />
            {valid && (
              <div className="text-xs text-emerald-400 mt-2">
                Shoppers will see {discount}% OFF on this product card.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Label
            </Label>
            <Input
              data-testid="deal-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-11 mt-1.5"
              placeholder="Deal of the Day"
            />
          </div>
          <Button
            data-testid="deal-submit"
            disabled={!valid || loading}
            onClick={submit}
            className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate deal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BOOST_PACKAGES = [
  {
    id: "boost_week",
    label: "Boost · 7 days",
    price: "$25",
    desc: "Top of search & home for one week.",
  },
  {
    id: "boost_month",
    label: "Boost · 30 days",
    price: "$80",
    desc: "Best value — keeps you up top for a month.",
    highlight: true,
  },
];

const BoostDialog = ({ product, onClose }) => {
  const [loadingPkg, setLoadingPkg] = useState(null);
  if (!product) return null;

  const checkout = async (pkgId) => {
    setLoadingPkg(pkgId);
    try {
      const { data } = await api.post("/dispensary/boost/checkout", {
        product_id: product.id,
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoadingPkg(null);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogTitle className="font-serif text-2xl">
          Boost {product.strain_name}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Boosted products jump to the top of every search and get a
          "Sponsored" badge that converts. You pick the duration.
        </DialogDescription>

        <div className="space-y-3 pt-2">
          {BOOST_PACKAGES.map((p) => (
            <button
              key={p.id}
              data-testid={`boost-pkg-${p.id}`}
              disabled={loadingPkg}
              onClick={() => checkout(p.id)}
              className={`w-full text-left rounded-2xl border p-4 hover:border-primary transition-colors flex items-center justify-between ${
                p.highlight
                  ? "border-primary/40 bg-primary/5"
                  : "border-border"
              }`}
            >
              <div>
                <div className="font-serif text-lg">{p.label}</div>
                <div className="text-xs text-muted-foreground">{p.desc}</div>
              </div>
              <div className="text-right">
                <div className="price-tag text-2xl">{p.price}</div>
                {loadingPkg === p.id && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto mt-1" />
                )}
              </div>
            </button>
          ))}
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center pt-2 flex items-center justify-center gap-2">
            <Clock className="w-3 h-3" /> Activates immediately after payment.
            Cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
